import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Spinner } from '@chakra-ui/react';

import { fetchPerspectiveGroup } from '../../apis/perspectives.api';
import PerspectivesHeader from './components/PerspectivesHeader';
import PerspectivesExplainerModal from './components/PerspectivesExplainerModal';
import PerspectivesAiDisclosure from './components/PerspectivesAiDisclosure';
import PersonaTabs from './components/PersonaTabs';
import AllResponsesPanel from './components/AllResponsesPanel';
import PerspectiveArticleContent from './components/PerspectiveArticleContent';
import CommunityArticleContent from './components/CommunityArticleContent';
import DiscourseSnapshot from './components/DiscourseSnapshot';
import WhereTheyAgree from './components/WhereTheyAgree';
import ReadingModeToggle from './components/ReadingModeToggle';
import {
  LearningModuleTriggerCard,
  LearningModuleModal,
  useLearningProgress
} from './components/LearningModule';
import LiveUpdatesBox from './components/LiveUpdatesBox';
import GeneralComment from '../ArticleViewer/components/GeneralComment';

const PerspectivesViewer = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const {
    data: group,
    isLoading,
    error
  } = useQuery({
    queryKey: ['perspectiveGroup', slug],
    queryFn: () => fetchPerspectiveGroup(slug!),
    enabled: !!slug,
    staleTime: 60_000
  });

  const perspectives = group?.perspectives || [];
  const _communityPerspectives = group?.communityPerspectives || [];
  const topCommunityPerspectives = group?.topCommunityPerspectives || [];
  const [activePerspectiveId, setActivePerspectiveId] = useState<string>('');
  const [activeCommunityId, setActiveCommunityId] = useState<string | null>(null);
  const [explainerModalOpen, setExplainerModalOpen] = useState(false);
  const [readingMode, setReadingMode] = useState<'brief' | 'full'>('brief');
  const [learningModalOpen, setLearningModalOpen] = useState(false);
  const articleContentRef = useRef<HTMLDivElement>(null);

  const learningModule = group?.learningModule ?? null;
  const {
    progress: learningProgress,
    markCardComplete,
    setStep: setLearningStep,
    completeQuiz,
    reset: resetLearning,
    isComplete: learningComplete,
    hasStarted: learningStarted
  } = useLearningProgress(slug);

  const scrollToContent = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        articleContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const handleSelectPerspective = (id: string) => {
    setActivePerspectiveId(id);
    setActiveCommunityId(null);
    setReadingMode('full');
    scrollToContent();
  };

  const handleSelectCommunity = (articleId: string) => {
    setActiveCommunityId(articleId);
    setActivePerspectiveId('');
    setReadingMode('full');
    scrollToContent();
  };

  useEffect(() => {
    if (perspectives.length > 0 && !activePerspectiveId && !activeCommunityId) {
      setActivePerspectiveId(perspectives[0].personaUserId);
    }
  }, [perspectives, activePerspectiveId, activeCommunityId]);

  const activePerspective =
    perspectives.find((p: any) => p.personaUserId === activePerspectiveId) ||
    (activeCommunityId ? null : perspectives[0]);
  const activeCommunityPerspective =
    topCommunityPerspectives.find((cp: any) => cp.articleId === activeCommunityId) ?? null;

  const hasArgumentBrief = activePerspective?.argumentBrief != null;

  if (isLoading) {
    return (
      <Box height="calc(100vh - 64px)" display="flex" alignItems="center" justifyContent="center">
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="green.500" size="xl" />
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box
        height="calc(100vh - 64px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        textAlign="center"
      >
        <div>
          <p className="text-lg text-slate-600 mb-2">Perspectives group not found.</p>
          <button
            onClick={() => navigate('/')}
            className="text-green-600 font-semibold hover:underline"
          >
            Back to Discovery
          </button>
        </div>
      </Box>
    );
  }

  const commentArticle = perspectives[0]?.articleId
    ? {
        id: perspectives[0].articleId,
        totalGeneralComments: group.generalCommentCount || 0,
        writingType: { name: 'Perspectives Analysis' }
      }
    : null;

  return (
    <div className="min-h-screen">
      {/* Event header */}
      <PerspectivesHeader
        category={group.category}
        publishedAt={group.publishedAt}
        title={group.title}
        summary={group.summary}
        onWriteResponse={() => {
          window.location.href = `/article/create?perspectiveGroupId=${group.id}&perspectiveGroupSlug=${slug}&perspectiveGroupTitle=${encodeURIComponent(group.title)}`;
        }}
      />

      {/* Live Updates */}
      {group.liveUpdates && group.liveUpdates.status !== 'inactive' && group.liveUpdates.updates?.length > 0 && (
        <LiveUpdatesBox liveUpdates={group.liveUpdates} slug={slug!} />
      )}

      {/* Discourse Snapshot */}
      {group.discourseSnapshot && (
        <DiscourseSnapshot
          snapshot={group.discourseSnapshot}
          keyDisagreement={group.keyDisagreement}
        />
      )}

      {/* Where They Agree */}
      {group.consensusPoints && group.consensusPoints.length > 0 && (
        <WhereTheyAgree consensusPoints={group.consensusPoints} />
      )}

      {/* Learning Module trigger card */}
      <LearningModuleTriggerCard
        module={learningModule}
        progress={learningProgress}
        isComplete={learningComplete}
        hasStarted={learningStarted}
        onOpen={() => setLearningModalOpen(true)}
      />

      {/* Learning Module modal */}
      {learningModule && (
        <LearningModuleModal
          isOpen={learningModalOpen}
          onClose={() => setLearningModalOpen(false)}
          module={learningModule}
          progress={learningProgress}
          onMarkCardComplete={markCardComplete}
          onSetStep={setLearningStep}
          onCompleteQuiz={completeQuiz}
          onReset={resetLearning}
        />
      )}

      <PerspectivesExplainerModal
        isOpen={explainerModalOpen}
        onClose={() => setExplainerModalOpen(false)}
      />

      {/* AI disclosure — just above persona tabs */}
      <div className="max-w-[812px] mx-auto px-4 md:px-0">
        <PerspectivesAiDisclosure onLearnMoreClick={() => setExplainerModalOpen(true)} />
      </div>

      {/* Persona tabs */}
      <PersonaTabs
        perspectives={perspectives}
        activePerspectiveId={activePerspectiveId}
        onSelect={handleSelectPerspective}
        communityPerspectives={topCommunityPerspectives}
        activeCommunityId={activeCommunityId}
        onSelectCommunity={handleSelectCommunity}
        groupId={group.id}
        groupSlug={slug}
        groupTitle={group.title}
      />

      {/* Bias space map */}
      {/* <BiasSpaceMap
        perspectives={perspectives}
        communityPerspectives={group.communityPerspectives || []}
        activePerspectiveId={activePerspectiveId}
        onSelect={handleSelectPerspective}
        onSelectCommunity={handleSelectCommunity}
        spectrumLeftLabel={group.spectrumLeftLabel}
        spectrumRightLabel={group.spectrumRightLabel}
      /> */}

      {/* Community responses panel */}
      <AllResponsesPanel
        slug={slug!}
        communityResponseCount={group.communityResponseCount || 0}
        groupId={group.id}
        groupSlug={slug}
        groupTitle={group.title}
      />

      {/* Reading mode toggle — only for AI perspectives with argument brief data */}
      {!activeCommunityPerspective && activePerspective && hasArgumentBrief && (
        <ReadingModeToggle
          personaName={activePerspective.personaName}
          readingMode={readingMode}
          onModeChange={setReadingMode}
        />
      )}

      {/* Article content */}
      <div
        ref={articleContentRef}
        className="max-w-[812px] mx-auto px-4 md:px-0 pt-9 pb-8 scroll-mt-16"
      >
        {activeCommunityPerspective ? (
          <CommunityArticleContent
            articleId={activeCommunityPerspective.articleId}
            perspective={activeCommunityPerspective}
          />
        ) : activePerspective ? (
          <PerspectiveArticleContent
            perspective={activePerspective}
            publishedAt={group.publishedAt}
            readingMode={hasArgumentBrief ? readingMode : 'full'}
            onSwitchToFull={() => setReadingMode('full')}
          />
        ) : null}
      </div>

      {/* Divider */}
      <Box height="1px" bg="gray.200" mx="auto" my={4} maxW="400px" opacity={0.6} />

      {/* Comments */}
      {commentArticle && (
        <div className="max-w-[812px] mx-auto px-4 md:px-0 pb-20">
          <GeneralComment
            showSensitiveContentCard={false}
            article={commentArticle}
            isShowSensitive={false}
            commentCount={group.generalCommentCount || 0}
            groupId={group.id}
          />
        </div>
      )}
    </div>
  );
};

export default PerspectivesViewer;
